import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { getStockLevelStyle } from '../utils/stockUtils';
import './AdminDashboard.css';
import './UserDashboard.css';

function UserDashboard({ user, onLogout }) {
  const isHindi = user?.language === 'hi';
  const t = (en, hi) => (isHindi ? hi : en);
  const [activeTab, setActiveTab] = useState('add');
  const [transactions, setTransactions] = useState([]);
  const [yarnTypes, setYarnTypes] = useState([]);
  const [unitNumbers, setUnitNumbers] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedYarnType, setSelectedYarnType] = useState('all');
  const [stockSearch, setStockSearch] = useState('');
  const [stockSort, setStockSort] = useState('none');
  const [showTotals, setShowTotals] = useState(false);
  const [editingStockNote, setEditingStockNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [settings, setSettings] = useState({
    stock_threshold_critical: '50',
    stock_threshold_low: '100',
    stock_threshold_medium: '200',
    color_indicators_enabled: 'true'
  });

  const [formData, setFormData] = useState({
    chemical_type_id: '',
    unit_number_id: '',
    type: 'in',
    quantity: '',
    bags: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [activeTab, selectedYarnType]);

  useEffect(() => {
    if (activeTab === 'history') {
      setHistoryPage(1);
    }
  }, [activeTab]);


  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [transactionsData, yarnTypesData, unitNumbersData, stockData, settingsData] = await Promise.all([
        api.getTransactions(),
        api.getChemicalTypes(),
        api.getUnitNumbers(),
        api.getStock(selectedYarnType === 'all' ? null : selectedYarnType),
        api.getSettings(),
      ]);
      setTransactions(transactionsData);
      setYarnTypes(yarnTypesData);
      setUnitNumbers(unitNumbersData);
      setStock(stockData);
      setSettings(settingsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.addTransaction({
        ...formData,
        unit_number_id: parseInt(formData.unit_number_id, 10),
        quantity: parseFloat(formData.quantity),
        bags: parseInt(formData.bags, 10),
      });
      setSuccess(t('Transaction added successfully!', 'लेनदेन सफलतापूर्वक जोड़ा गया!'));
      setFormData({
        chemical_type_id: '',
        unit_number_id: '',
        type: 'in',
        quantity: '',
        bags: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStockNote = (item) => {
    setEditingStockNote({
      chemical_type_id: item.chemical_type_id,
      chemical_type_name: item.chemical_type_name,
      notes: item.stock_notes || '',
    });
  };

  const handleSaveStockNote = async (e) => {
    e.preventDefault();
    if (!editingStockNote) return;

    try {
      await api.saveStockNote(
        editingStockNote.chemical_type_id,
        editingStockNote.notes
      );
      setEditingStockNote(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const getCurrentTimeZone = () => (
    user?.timezone || (user?.username === 'admin' ? 'America/Chicago' : 'Asia/Kolkata')
  );

  const formatDateOnly = (value, timeZone) => {
    if (!value) return '-';
    const date = new Date(value.includes('T') ? value : `${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      dateStyle: 'medium',
    }).format(date);
  };

  const handleExportStock = (stockData) => {
    if (!stockData || stockData.length === 0) {
      setError(t('No stock data to export', 'निर्यात के लिए कोई स्टॉक डेटा नहीं है'));
      return;
    }

    // Get chemical type name for filename
    const yarnTypeName = selectedYarnType === 'all' 
      ? 'All_Types' 
      : yarnTypes.find(yt => yt.id === parseInt(selectedYarnType))?.name || 'Stock';

    // Create CSV content
    const headers = [
      t('Chemical Type', 'रसायन प्रकार'),
      t('Total In (Kg)', 'कुल इन (किग्रा)'),
      t('Total Out (Kg)', 'कुल आउट (किग्रा)'),
      t('Total Barrels In', 'कुल बैरल इन'),
      t('Total Barrels Out', 'कुल बैरल आउट'),
      t('Current Stock (Kg)', 'वर्तमान स्टॉक (किग्रा)'),
      t('Current Barrels', 'वर्तमान बैरल'),
      t('Notes', 'नोट्स'),
    ];
    const csvContent = [
      headers.join(','),
      ...stockData.map(item => [
        `"${item.chemical_type_name || '-'}"`,
        item.total_in,
        item.total_out,
        item.total_bags_in,
        item.total_bags_out,
        item.current_stock,
        item.current_bags,
        `"${item.stock_notes || ''}"`
      ].join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `Stock_${yarnTypeName}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccess(t('Stock data exported successfully!', 'स्टॉक डेटा सफलतापूर्वक निर्यात हुआ!'));
  };

  const filteredStock = stock
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      return (item.chemical_type_name || '').toLowerCase().includes(query);
    })
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return 0;
    });

  const totalHistoryPages = Math.max(1, Math.ceil(transactions.length / historyPageSize));
  const safeHistoryPage = Math.min(historyPage, totalHistoryPages);
  const pagedHistory = transactions.slice(
    (safeHistoryPage - 1) * historyPageSize,
    safeHistoryPage * historyPageSize
  );

  return (
    <div className="dashboard user-dashboard">
      <header className="dashboard-header">
        <h1>🏭 Paras Foam User Dashboard</h1>
        <div className="user-info">
          <span>
            {user.username === 'admin' && user.real_name
              ? `${t('Welcome', 'स्वागत है')}, ${user.real_name} 👋`
              : `${t('Welcome', 'स्वागत है')}, ${user.username}`}
          </span>
          <button onClick={onLogout} className="logout-btn">{t('Logout', 'लॉग आउट')}</button>
        </div>
      </header>

      <div className="tabs">
        <button
          className={activeTab === 'add' ? 'active' : ''}
          onClick={() => setActiveTab('add')}
        >
          <span className="tab-icon" aria-hidden="true">➕</span>
          <span className="tab-label">{t('Add Transaction', 'लेनदेन जोड़ें')}</span>
        </button>
        <button
          className={activeTab === 'stock' ? 'active' : ''}
          onClick={() => setActiveTab('stock')}
        >
          <span className="tab-icon" aria-hidden="true">📦</span>
          <span className="tab-label">{t('Stock Overview', 'स्टॉक अवलोकन')}</span>
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          <span className="tab-icon" aria-hidden="true">🧾</span>
          <span className="tab-label">{t('Transaction History', 'लेनदेन इतिहास')}</span>
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="tab-content">
        {loading ? (
          <div className="loading">{t('Loading...', 'लोड हो रहा है...')}</div>
        ) : (
          <>
            {activeTab === 'add' && (
              <div className="add-transaction-section">
                <h2>{t('Add New Transaction', 'नया लेनदेन जोड़ें')}</h2>
                <form onSubmit={handleSubmit} className="transaction-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="chemical_type_id">{t('Chemical Type', 'रसायन प्रकार')} *</label>
                      <select
                        id="chemical_type_id"
                        name="chemical_type_id"
                        value={formData.chemical_type_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{t('Select Chemical Type', 'रसायन प्रकार चुनें')}</option>
                        {yarnTypes.map((yarnType) => (
                          <option key={yarnType.id} value={yarnType.id}>
                            {yarnType.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="unit_number_id">{t('Unit Number', 'यूनिट नंबर')} *</label>
                      <select
                        id="unit_number_id"
                        name="unit_number_id"
                        value={formData.unit_number_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{t('Select Unit Number', 'यूनिट नंबर चुनें')}</option>
                        {unitNumbers.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="type">{t('Type', 'प्रकार')} *</label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="in">{t('Stock In', 'स्टॉक इन')}</option>
                        <option value="out">{t('Stock Out', 'स्टॉक आउट')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="bags">{t('Number of Barrels', 'बैरलों की संख्या')} *</label>
                      <input
                        type="number"
                        id="bags"
                        name="bags"
                        step="1"
                        min="1"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.bags}
                        onChange={handleInputChange}
                        placeholder={t('Enter number of barrels', 'बैरलों की संख्या दर्ज करें')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quantity">{t('Quantity (Kg)', 'मात्रा (किग्रा)')} *</label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        step="0.01"
                        min="0.01"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        placeholder={t('Enter quantity in Kg', 'किग्रा में मात्रा दर्ज करें')}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="date">{t('Date', 'तारीख')} *</label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">{t('Notes', 'नोट्स')}</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder={t('Enter any additional notes (optional)', 'अतिरिक्त नोट्स दर्ज करें (वैकल्पिक)')}
                      rows="4"
                    />
                  </div>

                  <button type="submit" className="submit-btn">
                    {t('Add Transaction', 'लेनदेन जोड़ें')}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'stock' && (
              <div className="stock-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                  <h2>{t('Current Stock Levels', 'वर्तमान स्टॉक स्तर')}</h2>
                  <div className="filters-row">
                    <label htmlFor="yarn-type-filter">{t('Filter by Chemical Type', 'रसायन प्रकार के अनुसार फ़िल्टर करें')}:</label>
                    <select
                      id="yarn-type-filter"
                      value={selectedYarnType}
                      onChange={(e) => setSelectedYarnType(e.target.value)}
                      className="select-input"
                    >
                      <option value="all">{t('All Types', 'सभी प्रकार')}</option>
                      {yarnTypes.map((yarnType) => (
                        <option key={yarnType.id} value={yarnType.id}>
                          {yarnType.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={stockSort}
                      onChange={(e) => setStockSort(e.target.value)}
                      className="select-input"
                    >
                      <option value="none">{t('Sort: Default', 'क्रम: डिफ़ॉल्ट')}</option>
                      <option value="high">{t('Sort: Stock high → low', 'क्रम: स्टॉक अधिक → कम')}</option>
                      <option value="low">{t('Sort: Stock low → high', 'क्रम: स्टॉक कम → अधिक')}</option>
                    </select>
                    <label className="toggle-inline">
                      <input
                        type="checkbox"
                        checked={showTotals}
                        onChange={(e) => setShowTotals(e.target.checked)}
                      />
                      {t('Show In/Out totals', 'इन/आउट कुल दिखाएँ')}
                    </label>
                    <div className="search-field">
                      <input
                        type="text"
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        placeholder={t('Search chemical type...', 'रसायन प्रकार खोजें...')}
                        className="list-search"
                      />
                      {stockSearch && (
                        <button
                          type="button"
                          className="search-clear"
                          onClick={() => setStockSearch('')}
                          aria-label={t('Clear search', 'खोज साफ करें')}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {editingStockNote && (
                  <div className="modal-overlay" onClick={() => setEditingStockNote(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Stock Notes', 'स्टॉक नोट्स')} - {editingStockNote.chemical_type_name}</h3>
                      <form onSubmit={handleSaveStockNote}>
                        <div className="form-group">
                          <label>{t('Notes', 'नोट्स')}</label>
                          <textarea
                            value={editingStockNote.notes}
                            onChange={(e) =>
                              setEditingStockNote({
                                ...editingStockNote,
                                notes: e.target.value,
                              })
                            }
                            rows="5"
                            placeholder={t('Add notes about this stock item...', 'इस स्टॉक आइटम के बारे में नोट्स जोड़ें...')}
                          />
                        </div>
                        <div className="modal-actions">
                          <button type="submit">{t('Save', 'सहेजें')}</button>
                          <button type="button" onClick={() => setEditingStockNote(null)}>
                            {t('Cancel', 'रद्द करें')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {filteredStock.length === 0 ? (
                  <div className="empty-state">{t('No stock data available for the current filters.', 'वर्तमान फ़िल्टर के लिए कोई स्टॉक डेटा उपलब्ध नहीं है।')}</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('Chemical Type', 'रसायन प्रकार')}</th>
                      {showTotals && <th>{t('Total Barrels In', 'कुल बैरल इन')}</th>}
                      {showTotals && <th>{t('Total Barrels Out', 'कुल बैरल आउट')}</th>}
                      {showTotals && <th>{t('Total In (Kg)', 'कुल इन (किग्रा)')}</th>}
                      {showTotals && <th>{t('Total Out (Kg)', 'कुल आउट (किग्रा)')}</th>}
                      <th>{t('Current Barrels', 'वर्तमान बैरल')}</th>
                      <th>{t('Current Stock (Kg)', 'वर्तमान स्टॉक (किग्रा)')}</th>
                      <th>{t('Notes', 'नोट्स')}</th>
                    </tr>
                  </thead>
                  <tbody>
                        {filteredStock.map((item, index) => {
                          const colored = settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true;
                          return (
                            <tr key={`${item.chemical_type_id || 'none'}-${index}`}>
                              <td>{item.chemical_type_name || '-'}</td>
                              {showTotals && <td>{item.total_bags_in}</td>}
                              {showTotals && <td>{item.total_bags_out}</td>}
                              {showTotals && <td>{item.total_in}</td>}
                              {showTotals && <td>{item.total_out}</td>}
                              <td>{item.current_bags}</td>
                              <td style={getStockLevelStyle(item.current_stock, settings)}>
                                <div className={`stock-cell ${colored ? 'stock-cell--colored' : 'stock-cell--plain'}`}>
                                  <span className="stock-value">{item.current_stock}</span>
                                </div>
                              </td>
                              <td>
                                <button
                                  onClick={() => handleEditStockNote(item)}
                                  className="btn-edit"
                                  style={{ fontSize: '12px' }}
                                  title={item.stock_notes ? `${t('Notes', 'नोट्स')}: ${item.stock_notes}` : t('Add notes', 'नोट्स जोड़ें')}
                                >
                                  {item.stock_notes ? `📝 ${t('Edit', 'संपादित करें')}` : `➕ ${t('Add Note', 'नोट जोड़ें')}`}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-section">
                <h2>{t('Recent Transactions', 'हाल के लेनदेन')}</h2>
                {viewingNote && (
                  <div className="modal-overlay" onClick={() => setViewingNote(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Transaction Notes', 'लेनदेन नोट्स')}</h3>
                      <div className="form-group">
                        <p style={{ whiteSpace: 'pre-wrap', padding: '10px', background: '#f5f5f5', borderRadius: '4px', minHeight: '100px' }}>
                          {viewingNote || t('No notes', 'कोई नोट्स नहीं')}
                        </p>
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setViewingNote(null)}>
                          {t('Close', 'बंद करें')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {transactions.length === 0 ? (
                  <div className="empty-state">{t('No transactions yet.', 'अभी तक कोई लेनदेन नहीं है।')}</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                    <tr>
                      <th>{t('Date', 'तारीख')}</th>
                      <th>{t('Chemical Type', 'रसायन प्रकार')}</th>
                      <th>{t('Unit Number', 'यूनिट नंबर')}</th>
                      <th>{t('Type', 'प्रकार')}</th>
                      <th>{t('Barrels', 'बैरल')}</th>
                      <th>{t('Quantity (Kg)', 'मात्रा (किग्रा)')}</th>
                      <th>{t('Notes', 'नोट्स')}</th>
                      <th>{t('Created By', 'बनाने वाला')}</th>
                    </tr>
                      </thead>
                      <tbody>
                        {pagedHistory.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{formatDateOnly(transaction.date, getCurrentTimeZone())}</td>
                            <td>{transaction.chemical_type_name || '-'}</td>
                            <td>{transaction.unit_number_name || '-'}</td>
                        <td>
                          <span className={`badge ${transaction.type}`}>
                            {transaction.type === 'in'
                              ? t('IN', 'इन')
                              : transaction.type === 'out'
                                ? t('OUT', 'आउट')
                                : t('HOLD', 'होल्ड')}
                          </span>
                        </td>
                        <td>{transaction.bags}</td>
                        <td>{transaction.quantity}</td>
                            <td style={{ maxWidth: '200px' }}>
                              {transaction.notes && transaction.notes.length > 50 ? (
                                <>
                                  {transaction.notes.substring(0, 50)}...
                                  <button
                                    onClick={() => setViewingNote(transaction.notes)}
                                    className="btn-edit"
                                    style={{ marginLeft: '5px', fontSize: '11px', padding: '2px 6px' }}
                                  >
                                    {t('Show More', 'और देखें')}
                                  </button>
                                </>
                              ) : (
                                transaction.notes || '-'
                              )}
                            </td>
                            <td>{transaction.created_by_username}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {transactions.length > 0 && (
                  <div className="pagination-bar">
                    <div className="pagination-info">
                      {t('Page', 'पृष्ठ')} {safeHistoryPage} {t('of', 'का')} {totalHistoryPages}
                    </div>
                    <div className="pagination-controls">
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={safeHistoryPage === 1}
                      >
                        {t('Previous', 'पिछला')}
                      </button>
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                        disabled={safeHistoryPage === totalHistoryPages}
                      >
                        {t('Next', 'अगला')}
                      </button>
                      <select
                        className="page-size-select"
                        value={historyPageSize}
                        onChange={(e) => {
                          setHistoryPageSize(Number(e.target.value));
                          setHistoryPage(1);
                        }}
                      >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;
